import { Component, Input, OnInit, forwardRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { PaymentSystem } from '../../services/data/data-service.interface';
import { PaymentType } from '../form-fields/payment-account/enums/payment-types.enum';
import { skip } from 'rxjs/operators';

@Component({
  selector: 'gtd-currency-field',
  templateUrl: './currency-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CurrencyFieldComponent),
    },
  ],
})
@DestroySubscribers()
export class CurrencyFieldComponent implements ControlValueAccessor, OnInit {
  @Input() label = $localize`:@@common.currency:Currency`;
  @Input() required = true;
  @Input() readonly = false;
  @Input() data: PaymentSystem[];
  @Input() isDefaultPaymentControl: FormControl<string | boolean>;

  public control: UntypedFormControl;
  public dependControl: UntypedFormControl;
  public subscribers: Record<string, Subscription> = {};
  public value: string;
  public selectedData: string;
  public options = {};
  public originalValue: string | null = null;
  public isValueChanged: boolean = false;
  public createMode: boolean = false;
  public showWarning: boolean = false;

  private writeValueCallsCount = 0;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscribers.dependControl = this.dependControl.valueChanges.subscribe((value) => {
      if (this.data) {
        const options = this.data.find((paymentSystem) => paymentSystem.name === value)?.codes;

        if (options && !options[this.control.value]) {
          this.control.reset();
        }

        this.options = options;

        if (value === PaymentType.ERC20 || value === PaymentType.TRC20) {
          this.handleChange('USDT');
        }
      }

      this.cd.markForCheck();
    });
    this.subscribers.isValueChanged = this.control.valueChanges.subscribe((value) => {
      if (this.createMode) {
        return;
      }
      this.showWarning = this.originalValue !== value;
    });
    this.subscribers.defaultPayment = this.isDefaultPaymentControl.valueChanges.pipe(skip(1)).subscribe((value) => {
      this.showWarning = !!+value;
      this.cd.markForCheck();
    });
  }

  public onChange: (value: string) => void = () => {};
  public onTouch = () => {};

  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  handleChange(value: string): void {
    this.selectedData = value;
    this.control.patchValue(this.selectedData);
    this.cd.markForCheck();
  }

  writeValue(value: string): void {
    if (this.writeValueCallsCount === 1) {
      this.createMode = !isNaN(+value);
      this.originalValue = value;
    }
    this.selectedData = value;
    this.onChange(this.selectedData);
    this.cd.markForCheck();
    this.writeValueCallsCount += 1;
  }
}
